import setting from "../../../package.json";

const DEDUPE_TIME = 60 * 60 * 1000;
const DEDUPE_STORAGE_KEY = "lark_error_dedupe_cache";
const DEFAULT_INSTALL_OPTIONS = {
  vue: true,
  js: true,
  promise: true,
  dev: false,// * 開發環境下不上報錯誤
};
const dedupeCache = {};

let installed = false;
let oldVueErrorHandler = null;
let promiseCallbackHandlerInstalled = false;
let oldPromiseThen = null;
let oldPromiseCatch = null;
let currentInstallOptions = { ...DEFAULT_INSTALL_OPTIONS };

// ? 判斷是否開發環境
function isDevelopment() {
  if (currentInstallOptions.dev) return false;
  return process.env.NODE_ENV === "development";
}

// ? 生成安裝配置
function getInstallOptions(options) {
  return {
    ...DEFAULT_INSTALL_OPTIONS,
    ...(options || {}),
  };
}

// ? 安全轉換任意內容為字符串
function safeStringify(value) {
  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
}

// ? 生成短 hash 作為去重 key
function hashText(text) {
  let hash = 0;
  const source = String(text);

  for (let index = 0; index < source.length; index++) {
    hash = (hash << 5) - hash + source.charCodeAt(index);
    hash |= 0;
  }

  return String(hash);
}

// ? 安全讀取本地緩存
function getLocalStorageItem(key) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return "";
  }
}

// ? 讀取本地去重緩存
function getStorageCache() {
  try {
    return JSON.parse(localStorage.getItem(DEDUPE_STORAGE_KEY) || "{}");
  } catch (error) {
    return {};
  }
}

// ? 寫入本地去重緩存
function setStorageCache(cache) {
  try {
    localStorage.setItem(DEDUPE_STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    // * localStorage 不可用時只使用內存去重，不影響錯誤上報主流程
  }
}

// ? 清理過期去重記錄
function cleanExpiredCache(cache, now) {
  Object.keys(cache).forEach((key) => {
    if (now - cache[key] > DEDUPE_TIME) {
      delete cache[key];
    }
  });

  return cache;
}

// ? 生成錯誤去重 key
function getErrorDedupeKey(errorInfo) {
  const keyInfo = {
    type: errorInfo.type,
    name: errorInfo.name,
    message: errorInfo.message,
    stack: errorInfo.stack,
    info: errorInfo.info,
    componentName: errorInfo.componentName,
    apiUrl: errorInfo.apiUrl,
    apiMethod: errorInfo.apiMethod,
    status: errorInfo.status,
    code: errorInfo.code,
  };

  return hashText(safeStringify(keyInfo));
}

// ? 判斷一小時內是否已記錄過同一個錯誤
function shouldSkipRepeat(errorInfo) {
  const now = Date.now();
  const key = getErrorDedupeKey(errorInfo);
  const storageCache = cleanExpiredCache(getStorageCache(), now);

  if (
    (dedupeCache[key] && now - dedupeCache[key] < DEDUPE_TIME) ||
    (storageCache[key] && now - storageCache[key] < DEDUPE_TIME)
  ) {
    return true;
  }

  dedupeCache[key] = now;
  storageCache[key] = now;
  setStorageCache(storageCache);

  return false;
}

// ? 格式化 Error 對象
function formatError(error) {
  if (!error) {
    return {
      name: "UnknownError",
      message: "未知錯誤",
      stack: "",
    };
  }

  if (error instanceof Error) {
    return {
      name: error.name || "Error",
      message: error.message || "未知錯誤",
      stack: error.stack || "",
    };
  }

  if (typeof error === "string") {
    return {
      name: "StringError",
      message: error,
      stack: "",
    };
  }

  return {
    name: error.name || "ObjectError",
    message: error.message || safeStringify(error),
    stack: error.stack || "",
  };
}

// ? 組裝通用錯誤信息
function createErrorInfo(type, error, extra) {
  const baseError = formatError(error);

  return {
    type: type,
    name: baseError.name,
    message: baseError.message,
    stack: baseError.stack,
    page: window.location.pathname + window.location.search,
    href: window.location.href,
    userAgent: navigator.userAgent,
    time: new Date().toLocaleString(),
    version: setting.version,
    application: window.location.pathname.split("/")[1],
    ...(extra || {}),
  };
}

// ? 組裝接口錯誤信息
function createApiErrorInfo(error) {
  const response = error && error.response ? error.response : error;
  const config = (error && error.config) || (response && response.config) || {};
  const data = response && response.data ? response.data : null;
  const message =
    (data && (data.msg || data.message || data.error)) ||
    (error && error.message) ||
    "接口異常";

  return createErrorInfo("api", error, {
    message: message,
    status: response && response.status,
    statusText: response && response.statusText,
    code: data && data.code,
    apiUrl: config.url || "",
    apiMethod: config.method || "",
    apiParams: config.params || "",
    apiData: config.data || "",
    responseData: data || "",
  });
}

// ? 取得當前用戶與應用信息
function getCommonInputs(errorInfo) {
  const pathname = window.location.pathname;
  const search = window.location.search;
  const application = pathname.split("/")[1];
  const memberId = getLocalStorageItem("app_user_id");
  const phone = getLocalStorageItem("app_user_phone");
  const memberToken = getLocalStorageItem("random_word");

  return {
    page: pathname + search,
    errorText: safeStringify(errorInfo),
    phone: phone,
    memberToken: memberToken,
    memberId: memberId,
    time: new Date().toLocaleString(),
    version: setting.version,
    application: application,
    user: memberId || phone || memberToken || application + "-default-member",
  };
}

const larkError = {
  // ? 初始化 Vue、JS、Promise 全局異常捕獲
  install(Vue, options) {
    const installOptions = getInstallOptions(options);
    currentInstallOptions = installOptions;

    if (isDevelopment()) return;

    if (installed) return;

    installed = true;

    if (installOptions.vue) {
      this.installVueHandler(Vue);
    }

    if (installOptions.js) {
      this.installJsHandler();
    }

    if (installOptions.promise) {
      this.installPromiseHandler();
      this.installPromiseCallbackHandler();
    }
  },

  // ? 捕獲 Vue 組件異常
  installVueHandler(Vue) {
    if (!Vue || !Vue.config) return;

    oldVueErrorHandler = Vue.config.errorHandler;
    Vue.config.errorHandler = (error, vm, info) => {
      this.log(
        createErrorInfo("vue", error, {
          info: info,
          componentName: vm && vm.$options ? vm.$options.name : "",
          route:
            vm && vm.$route
              ? {
                  path: vm.$route.path,
                  fullPath: vm.$route.fullPath,
                  name: vm.$route.name,
                }
              : "",
        })
      );

      if (typeof oldVueErrorHandler === "function") {
        oldVueErrorHandler(error, vm, info);
      }
    };
  },

  // ? 捕獲普通 JS 異常
  installJsHandler() {
    window.addEventListener(
      "error",
      (event) => {
        const target = event.target || event.srcElement;

        if (target && target !== window) {
          // * 不記錄 img/script/link 等 HTML 資源載入異常
          return;
        }

        this.log(
          createErrorInfo("js", event.error || event.message, {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          })
        );
      },
      true
    );
  },

  // ? 捕獲未處理 Promise 異常
  installPromiseHandler() {
    window.addEventListener("unhandledrejection", (event) => {
      const reason = event.reason || "未處理 Promise 異常";

      if (reason && (reason.__larkApiLogged || reason.__larkJsLogged)) return;

      this.log(
        createErrorInfo("js", reason, {
          info: "unhandledrejection",
        })
      );
    });
  },

  // ? 捕獲 Promise 回調內拋出的 JS 異常
  installPromiseCallbackHandler() {
    if (promiseCallbackHandlerInstalled || typeof Promise === "undefined") {
      return;
    }

    promiseCallbackHandlerInstalled = true;
    oldPromiseThen = Promise.prototype.then;
    oldPromiseCatch = Promise.prototype.catch;

    Promise.prototype.then = function (onFulfilled, onRejected) {
      return oldPromiseThen.call(
        this,
        larkError.wrapPromiseCallback(onFulfilled, "promise.then fulfilled"),
        larkError.wrapPromiseCallback(onRejected, "promise.then rejected")
      );
    };

    Promise.prototype.catch = function (onRejected) {
      return oldPromiseCatch.call(
        this,
        larkError.wrapPromiseCallback(onRejected, "promise.catch rejected")
      );
    };
  },

  // ? 包裝 Promise 回調並在拋錯時記錄
  wrapPromiseCallback(callback, info) {
    if (typeof callback !== "function") return callback;

    return function (...args) {
      try {
        return callback.apply(this, args);
      } catch (error) {
        if (error && typeof error === "object") {
          // * 標記已在 Promise 回調中記錄，避免後續 unhandledrejection 重複上報
          error.__larkJsLogged = true;
        }

        larkError.log(
          createErrorInfo("js", error, {
            info: info,
          })
        );
        throw error;
      }
    };
  },

  // ? 記錄接口異常
  logApi(error) {
    if (error && typeof error === "object") {
      error.__larkApiLogged = true;

      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        error.response.data.__larkApiLogged = true;
      }

      if (error.data && typeof error.data === "object") {
        error.data.__larkApiLogged = true;
      }
    }

    this.log(createApiErrorInfo(error));
  },

  // ? 記錄並上報異常
  log(errorText) {
    if (isDevelopment()) return;

    const errorInfo =
      errorText && errorText.type
        ? errorText
        : createErrorInfo("manual", errorText);

    if (shouldSkipRepeat(errorInfo)) {
      console.log("跳過重複異常", errorInfo);
      return;
    }

    console.error("----------------捕獲全局異常----------------");
    console.error(errorInfo);
    console.error("----------------全局異常結束----------------");

    this.send(errorInfo);
  },

  // ? 上報異常到 Lark 工作流
  send(errorInfo) {
    const inputs = getCommonInputs(errorInfo);

    fetch("https://dify.ricepon.ai/v1/workflows/run", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer app-9viSivro1w6gNQzLsMMmEya7",
      },
      body: JSON.stringify({
        inputs: {
          page: inputs.page,
          errorText: inputs.errorText,
          phone: inputs.phone,
          memberToken: inputs.memberToken,
          memberId: inputs.memberId,
          time: inputs.time,
          version: inputs.version,
          application: inputs.application,
          env: process.env.NODE_ENV,
        },
        response_mode: "blocking",
        user: inputs.user,
      }),
    }).catch((error) => {
      // * 上報自身失敗只輸出控制台，避免再次進入全局異常捕獲形成循環
      console.error("larkError 上報失敗", error);
    });
  },
};

export default larkError;
